import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { 
  H1, 
  Box, 
  Button, 
  InlineMessage, 
  Form, 
  FormGroup, 
  Input, 
  Select, 
  Panel, 
  Flex, 
  Text,
  Grid,
  GridItem,
  Checkbox,
  Table
} from '@bigcommerce/big-design';
import { CheckCircleIcon, CloseIcon, ArrowBackIcon, ChevronLeftIcon, ChevronRightIcon } from '@bigcommerce/big-design-icons';
import { createFee, updateFee, getFees, getTaxClasses, getCategories, getProducts, getBrands, getChannels, getCustomerGroups } from '../api/fees';

const formatValue = (value) => {
  const num = Number(value);
  return Number.isInteger(num) ? num.toString() : value.toString();
};

// Recursive CategoryTree component
const CategoryTree = ({ categories, selectedCategories, onCategoryToggle, level = 0 }) => {
  return categories.map(category => (
    <Box key={category.id} marginLeft={level > 0 ? "medium" : "none"}>
      <Checkbox
        label={category.name}
        checked={selectedCategories.includes(category.id)}
        onChange={() => onCategoryToggle(category.id)}
        marginBottom="small"
      />
      {category.children && category.children.length > 0 && (
        <CategoryTree 
          categories={category.children}
          selectedCategories={selectedCategories}
          onCategoryToggle={onCategoryToggle}
          level={level + 1}
        />
      )}
    </Box>
  ));
};

// Enforce positive numeric input
const handlePositiveNumberChange = (value, setter) => {
  const cleanValue = value.replace(/[^0-9.]/g, '');
  const parts = cleanValue.split('.');
  const formattedValue = parts.length > 2
    ? parts[0] + '.' + parts.slice(1).join('')
    : cleanValue;
  if (!formattedValue || parseFloat(formattedValue) >= 0) {
    setter(formattedValue);
  }
};

function CreateEditFeePage() {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = Boolean(id);
  
  // Form state
  const [feeName, setFeeName] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [feeType, setFeeType] = useState("fixed");
  const [feeAmount, setFeeAmount] = useState("");
  const [feeEnabled, setFeeEnabled] = useState(true);
  const [taxClassId, setTaxClassId] = useState("");
  const [applyFeePer, setApplyFeePer] = useState("order");
  const [percentageOf, setPercentageOf] = useState("subtotal_excluding_tax");
  
  // UI state
  const [isLoading, setIsLoading] = useState(isEdit);
  const [isSaving, setIsSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [taxClasses, setTaxClasses] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [productSearch, setProductSearch] = useState('');
  const [productPage, setProductPage] = useState(1);
  const [productPagination, setProductPagination] = useState({});
  const [isLoadingProducts, setIsLoadingProducts] = useState(false);
  
  const [brands, setBrands] = useState([]);
  const [selectedBrands, setSelectedBrands] = useState([]);
  const [brandSearch, setBrandSearch] = useState('');
  const [brandPage, setBrandPage] = useState(1);
  const [brandPagination, setBrandPagination] = useState({});
  const [isLoadingBrands, setIsLoadingBrands] = useState(false);
  
  const [channels, setChannels] = useState([]);
  const [selectedChannels, setSelectedChannels] = useState([]);
  
  const [customerGroups, setCustomerGroups] = useState([]);
  const [selectedCustomerGroups, setSelectedCustomerGroups] = useState([]);

  useEffect(() => {
    const loadData = async () => {
      // Load tax classes first, then fee data to ensure proper option mapping
      await loadTaxClasses();
      loadCategories();
      loadProducts();
      loadBrands();
      loadChannels();
      loadCustomerGroups();
      if (isEdit) {
        loadFeeData();
      }
    };
    loadData();
  }, [id, isEdit]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadTaxClasses = async () => {
    try {
      const classes = await getTaxClasses();
      setTaxClasses(classes);
    } catch (error) {
      console.error("Failed to load tax classes", error);
      // Fallback to empty array if tax classes can't be loaded
    }
  };

  const loadCategories = async () => {
    try {
      const categoriesData = await getCategories();
      setCategories(categoriesData);
    } catch (error) {
      console.error("Failed to load categories", error);
      // Fallback to empty array if categories can't be loaded
    }
  };

  const loadProducts = async (page = 1, search = '') => {
    try {
      setIsLoadingProducts(true);
      const response = await getProducts({ page, limit: 10, search });
      setProducts(response.products);
      setProductPagination(response.pagination);
    } catch (error) {
      console.error("Failed to load products", error);
      // Fallback to empty array if products can't be loaded
    } finally {
      setIsLoadingProducts(false);
    }
  };

  const loadBrands = async (page = 1, search = '') => {
    try {
      setIsLoadingBrands(true);
      const response = await getBrands({ page, limit: 10, search });
      setBrands(response.brands);
      setBrandPagination(response.meta.pagination || {});
    } catch (error) {
      console.error("Failed to load brands", error);
      // Fallback to empty array if brands can't be loaded
    } finally {
      setIsLoadingBrands(false);
    }
  };

  const loadChannels = async () => {
    try {
      const response = await getChannels();
      if (response.error) {
        // Handle permission error gracefully
        console.warn("Channels not available:", response.error);
        setChannels([]);
        return;
      }
      setChannels(response.channels || []);
    } catch (error) {
      console.error("Failed to load channels", error);
      // Fallback to empty array if channels can't be loaded
      setChannels([]);
    }
  };

  const loadCustomerGroups = async () => {
    try {
      const response = await getCustomerGroups();
      if (response.error) {
        // Handle permission error gracefully
        console.warn("Customer groups not available:", response.error);
        setCustomerGroups([]);
        return;
      }
      setCustomerGroups(response.customer_groups || []);
    } catch (error) {
      console.error("Failed to load customer groups", error);
      // Fallback to empty array if customer groups can't be loaded
      setCustomerGroups([]);
    }
  };

  const fetchBrandNamesForIds = async (brandIds) => {
    try {
      let allBrands = [];
      let page = 1;
      let hasMorePages = true;
      
      // Fetch all brands with pagination
      while (hasMorePages) {
        const response = await getBrands({ page: page, limit: 250 });
        allBrands = allBrands.concat(response.brands);
        
        // Check if there are more pages
        const totalPages = response.meta?.pagination?.total_pages || 1;
        hasMorePages = page < totalPages;
        page++;
      }
      
      // Filter to only brands that match our IDs
      const matchingBrands = allBrands.filter(brand => brandIds.includes(brand.id));
      
      // Set the selected brands with both id and name
      const brandData = matchingBrands.map(brand => ({
        id: brand.id,
        name: brand.name
      }));
      
      setSelectedBrands(brandData);
    } catch (error) {
      console.error("Failed to fetch brand names", error);
      // Fallback: set brands with just IDs (will show blank names but won't crash)
      const fallbackData = brandIds.map(id => ({
        id: id,
        name: `Brand ${id}` // Fallback name
      }));
      setSelectedBrands(fallbackData);
    }
  };

  const loadFeeData = async () => {
    try {
      setIsLoading(true);
      const fees = await getFees();
      const fee = fees.find(f => f.id === parseInt(id));
      
      if (fee) {
        setFeeName(fee.name);
        setDisplayName(fee.display_name || fee.name);
        setFeeType(fee.fee_type === "amount" ? "fixed" : "percentage");
        setFeeAmount(formatValue(fee.amount));
        setFeeEnabled(fee.enabled);
        setTaxClassId(fee.tax_class_id !== null && fee.tax_class_id !== undefined ? fee.tax_class_id.toString() : "");
        setApplyFeePer(fee.apply_fee_per || "order");
        setPercentageOf(fee.percentage_of || "subtotal_excluding_tax");
        
        // Load selected categories if fee has any
        if (fee.fee_categories && fee.fee_categories.length > 0) {
          const categoryIds = fee.fee_categories.map(fc => fc.category_id);
          setSelectedCategories(categoryIds);
        }

        // Load selected products if fee has any
        if (fee.fee_products && fee.fee_products.length > 0) {
          const productData = fee.fee_products.map(fp => ({
            id: fp.product_id,
            name: fp.name,
            sku: fp.sku
          }));
          setSelectedProducts(productData);
        }
        
        // Load selected brands if fee has any
        if (fee.fee_brands && fee.fee_brands.length > 0) {
          // We need to fetch brand names since fee_brands only stores brand_id
          const brandIds = fee.fee_brands.map(fb => fb.brand_id);
          await fetchBrandNamesForIds(brandIds);
        }
        
        // Load selected channels if fee has any
        if (fee.fee_channels && fee.fee_channels.length > 0) {
          const channelData = fee.fee_channels.map(fc => ({
            id: fc.channel_id,
            name: fc.channel_name
          }));
          setSelectedChannels(channelData);
        }
        
        // Load selected customer groups if fee has any
        if (fee.fee_customer_groups && fee.fee_customer_groups.length > 0) {
          const customerGroupData = fee.fee_customer_groups.map(fcg => ({
            id: fcg.customer_group_id,
            name: fcg.customer_group_name
          }));
          setSelectedCustomerGroups(customerGroupData);
        }
      } else {
        setErrorMessage("Fee not found");
      }
    } catch (error) {
      console.error("Failed to load fee", error);
      setErrorMessage("Failed to load fee data");
    } finally {
      setIsLoading(false);
    }
  };

  const handleSave = async () => {
    // Validation
    if (!feeName.trim()) {
      setErrorMessage("Fee name is required");
      return;
    }
    if (!displayName.trim()) {
      setErrorMessage("Display name is required");
      return;
    }
    if (!feeAmount || parseFloat(feeAmount) <= 0) {
      setErrorMessage("Amount must be greater than 0");
      return;
    }

    setIsSaving(true);
    setErrorMessage("");

    try {
      const feeData = {
        name: feeName.trim(),
        display_name: displayName.trim(),
        amount: parseFloat(feeAmount),
        enabled: feeEnabled,
        fee_type: feeType === "fixed" ? "amount" : "percentage",
        tax_class_id: taxClassId || null,
        apply_fee_per: feeType === "fixed" ? applyFeePer : null,
        percentage_of: feeType === "percentage" ? percentageOf : null,
        categories: selectedCategories,
        products: selectedProducts,
        brands: selectedBrands,
        channels: selectedChannels,
        customer_groups: selectedCustomerGroups,
      };

      if (isEdit) {
        await updateFee(parseInt(id), feeData);
        localStorage.setItem('feeSuccessMessage', "Fee updated successfully!");
      } else {
        await createFee(feeData);
        localStorage.setItem('feeSuccessMessage', "Fee created successfully!");
      }

      // Navigate back immediately
      navigate('/fees');

    } catch (error) {
      console.error("Failed to save fee", error);
      
      // Extract specific validation errors from the API response
      let errorMsg = isEdit ? "Failed to update fee" : "Failed to create fee";
      
      if (error.response && error.response.data) {
        const errors = error.response.data;
        if (typeof errors === 'object') {
          // Format validation errors into readable messages
          const errorMessages = [];
          Object.keys(errors).forEach(field => {
            const fieldErrors = Array.isArray(errors[field]) ? errors[field] : [errors[field]];
            fieldErrors.forEach(message => {
              errorMessages.push(`${field.charAt(0).toUpperCase() + field.slice(1)}: ${message}`);
            });
          });
          
          if (errorMessages.length > 0) {
            errorMsg = errorMessages.join('. ');
          }
        } else if (typeof errors === 'string') {
          errorMsg = errors;
        }
      }
      
      setErrorMessage(errorMsg);
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    navigate('/fees');
  };

  // Category handling functions
  const handleCategoryToggle = (categoryId) => {
    setSelectedCategories(prev => {
      if (prev.includes(categoryId)) {
        return prev.filter(id => id !== categoryId);
      } else {
        return [...prev, categoryId];
      }
    });
  };

  const handleSelectAllCategories = () => {
    const allCategoryIds = getAllCategoryIds(categories);
    setSelectedCategories(allCategoryIds);
  };

  const handleDeselectAllCategories = () => {
    setSelectedCategories([]);
  };

  const getAllCategoryIds = (categoriesArray) => {
    let ids = [];
    categoriesArray.forEach(category => {
      ids.push(category.id);
      if (category.children && category.children.length > 0) {
        ids = ids.concat(getAllCategoryIds(category.children));
      }
    });
    return ids;
  };

  // Product handling functions
  const handleProductToggle = (product) => {
    setSelectedProducts(prev => {
      const existingProduct = prev.find(p => p.id === product.id);
      if (existingProduct) {
        return prev.filter(p => p.id !== product.id);
      } else {
        return [...prev, {
          id: product.id,
          name: product.name,
          sku: product.sku
        }];
      }
    });
  };

  const handleProductSearch = async (searchTerm) => {
    setProductSearch(searchTerm);
    setProductPage(1);
    await loadProducts(1, searchTerm);
  };

  const handleProductPageChange = async (newPage) => {
    setProductPage(newPage);
    await loadProducts(newPage, productSearch);
  };

  const handleClearSelectedProducts = () => {
    setSelectedProducts([]);
  };

  // Brand handling functions
  const handleBrandToggle = (brand) => {
    setSelectedBrands(prev => {
      const existingBrand = prev.find(b => b.id === brand.id);
      if (existingBrand) {
        return prev.filter(b => b.id !== brand.id);
      } else {
        return [...prev, {
          id: brand.id,
          name: brand.name
        }];
      }
    });
  };

  const handleBrandSearch = async (searchTerm) => {
    setBrandSearch(searchTerm);
    setBrandPage(1);
    await loadBrands(1, searchTerm);
  };

  const handleBrandPageChange = async (newPage) => {
    setBrandPage(newPage);
    await loadBrands(newPage, brandSearch);
  };

  const handleClearSelectedBrands = () => {
    setSelectedBrands([]);
  };

  // Channel handling functions
  const handleChannelToggle = (channel) => {
    setSelectedChannels(prev => {
      const existingChannel = prev.find(c => c.id === channel.id);
      if (existingChannel) {
        return prev.filter(c => c.id !== channel.id);
      } else {
        return [...prev, {
          id: channel.id,
          name: channel.name
        }];
      }
    });
  };

  const handleClearSelectedChannels = () => {
    setSelectedChannels([]);
  };

  // Customer group handling functions
  const handleCustomerGroupToggle = (group) => {
    setSelectedCustomerGroups(prev => {
      const existingGroup = prev.find(g => g.id === group.id);
      if (existingGroup) {
        return prev.filter(g => g.id !== group.id);
      } else {
        return [...prev, {
          id: group.id,
          name: group.name
        }];
      }
    });
  };

  const handleClearSelectedCustomerGroups = () => {
    setSelectedCustomerGroups([]);
  };

  if (isLoading) {
    return (
      <Box margin="xxLarge" padding="large">
        <Flex flexDirection="column" alignItems="center" justifyContent="center" style={{ minHeight: '400px' }}>
          <Box marginBottom="medium">
            <div style={{
              border: '4px solid #f3f3f3',
              borderTop: '4px solid #3C64F4',
              borderRadius: '50%',
              width: '40px',
              height: '40px',
              animation: 'spin 1s linear infinite'
            }} />
          </Box>
          <Text fontSize="large" color="secondary60">Loading fee data...</Text>
        </Flex>
        <style>{`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}</style>
      </Box>
    );
  }

  return (
    <Box margin="xxLarge" padding="large">
      {/* Header with Back Button */}
      <Flex alignItems="center" marginBottom="large">
        <Button
          variant="subtle"
          iconLeft={<ArrowBackIcon />}
          onClick={handleCancel}
          marginRight="medium"
        >
          Back
        </Button>
        <H1 margin="none">{isEdit ? 'Edit Fee' : 'Create Fee'}</H1>
      </Flex>

      {/* Error Messages */}
      {errorMessage && (
        <InlineMessage 
          type="error" 
          messages={[{ text: errorMessage }]} 
          marginBottom="large"
        />
      )}

      {/* Status Panel */}
      <Panel header="Status" marginBottom="large">
        <Flex alignItems="center" marginBottom="medium">
          {feeEnabled ? (
            <CheckCircleIcon color="success" marginRight="small" />
          ) : (
            <CloseIcon color="danger" marginRight="small" />
          )}
          <Text color={feeEnabled ? 'success' : 'danger'}>
            {feeEnabled 
              ? 'The fee is enabled and will be applied to orders.'
              : 'The fee is disabled and will not be applied to orders.'}
          </Text>
        </Flex>
        <Button
          variant={feeEnabled ? 'secondary' : 'primary'}
          onClick={() => setFeeEnabled(!feeEnabled)}
        >
          {feeEnabled ? 'Disable' : 'Enable'}
        </Button>
      </Panel>

      {/* Details Panel */}
      <Panel header="Details" marginBottom="large">
        <Text color="secondary60" marginBottom="medium">
          Enter the details for the fee
        </Text>
        
        <Form>
          <Grid gridColumns="1fr 1fr" gridGap="large" marginBottom="medium">
            <GridItem>
              <FormGroup>
                <Input
                  label="Name"
                  description="The name of the fee that will be shown in the admin panel. (Max 40 characters)"
                  value={feeName}
                  onChange={(e) => setFeeName(e.target.value)}
                  required
                  maxLength={40}
                  error={feeName.length > 40 ? "Name must be 40 characters or less" : null}
                />
              </FormGroup>
            </GridItem>
            <GridItem>
              <FormGroup>
                <Input
                  label="Display Name"
                  description="The line item name that will be shown to the customer. (Max 50 characters)"
                  value={displayName}
                  onChange={(e) => setDisplayName(e.target.value)}
                  required
                  maxLength={50}
                  error={displayName.length > 50 ? "Display name must be 50 characters or less" : null}
                />
              </FormGroup>
            </GridItem>
          </Grid>

          <Grid gridColumns="1fr 1fr" gridGap="large" marginBottom="medium">
            <GridItem>
              <FormGroup>
                <Select
                  label="Type"
                  description="Select the type of fee to apply"
                  options={[
                    { value: "fixed", content: "Fixed Amount" },
                    { value: "percentage", content: "Percentage" }
                  ]}
                  value={feeType}
                  onOptionChange={(val) => setFeeType(val)}
                  required
                />
              </FormGroup>
            </GridItem>
            <GridItem>
              <FormGroup>
                <Input
                  label={feeType === "percentage" ? "Percentage" : "Amount"}
                  description={feeType === "percentage" ? "Enter percentage value" : "Enter dollar amount"}
                  type="number"
                  min="0"
                  step="0.01"
                  placeholder={feeType === "percentage" ? "e.g., 3.5" : "e.g., 5.00"}
                  value={feeAmount}
                  onChange={(e) => handlePositiveNumberChange(e.target.value, setFeeAmount)}
                  required
                  iconLeft={feeType === "fixed" ? "$" : undefined}
                  iconRight={feeType === "percentage" ? "%" : undefined}
                />
              </FormGroup>
            </GridItem>
          </Grid>

          {feeType === "fixed" ? (
            <Box marginBottom="medium" key="fixed-fee-options">
              <FormGroup>
                <Select
                  label="Apply Fee Per"
                  description="Choose how to apply the fee when multiple products match"
                  options={[
                    { value: "order", content: "Order" },
                    { value: "product", content: "Product" }
                  ]}
                  value={applyFeePer}
                  onOptionChange={(val) => setApplyFeePer(val)}
                  required
                />
              </FormGroup>
              <Text color="secondary60" marginTop="xSmall">
                <strong>{applyFeePer === "order" ? "Order:" : "Product:"}</strong>{" "}
                {applyFeePer === "order"
                  ? "Regardless of how many matched products there are, the fee will be charged once."
                  : "The fee will be charged once for each matched product."}
              </Text>
            </Box>
          ) : (
            <Box marginBottom="medium" key="percentage-fee-options">
              <Select
                label="Percentage Of"
                description="Select the base amount for percentage calculation"
                options={[
                  { value: "subtotal_including_tax", content: "Subtotal Including Tax" },
                  { value: "subtotal_excluding_tax", content: "Subtotal Excluding Tax" },
                  { value: "grand_total", content: "Grand Total" },
                  { value: "amount_due", content: "Amount Due" },
                  { value: "applicable_products_default", content: "Applicable Products (Default Price x Quantity)" },
                  { value: "applicable_products_discounted", content: "Applicable Products (Discounted Price x Quantity)" },
                  { value: "applicable_products_msrp", content: "Applicable Products (MSRP x Quantity)" }
                ]}
                value={percentageOf}
                onOptionChange={(val) => setPercentageOf(val)}
                placeholder="Select percentage base..."
                required
              />
              <Text color="secondary60" marginTop="xSmall">
                <strong>Percentage Base:</strong>{" "}
                {percentageOf === "subtotal_including_tax" && "Subtotal of checkout before discounts including tax."}
                {percentageOf === "subtotal_excluding_tax" && "Subtotal of checkout before discounts excluding tax."}
                {percentageOf === "grand_total" && "Total payable amount before store credit or gift certificates."}
                {percentageOf === "amount_due" && "Total payable amount after store credit or gift certificates."}
                {percentageOf === "applicable_products_default" && "Total cost (price x quantity) of matched products before discounts."}
                {percentageOf === "applicable_products_discounted" && "Total cost (sale price x quantity) of matched products after discounts."}
                {percentageOf === "applicable_products_msrp" && "Total cost (MSRP x quantity) of matched products."}
              </Text>
            </Box>
          )}

        </Form>
      </Panel>

      {/* Tax Configuration Panel */}
      <Panel header="Tax Configuration" marginBottom="large">
        <Text color="secondary60" marginBottom="medium">
          Select how this fee should be taxed, or choose 'No Tax Class (Use Default)' if no taxes should apply.
        </Text>
        
        <Form>
          <FormGroup>
            <Select
              label="Tax Class"
              description="Select how this fee should be taxed"
              options={taxClasses.map(tc => ({ 
                value: tc.id, 
                content: tc.name 
              }))}
              value={taxClassId}
              onOptionChange={(val) => setTaxClassId(val)}
              placeholder="Select tax class..."
            />
          </FormGroup>
        </Form>
      </Panel>

      {/* Categories Panel */}
      <Panel header="Categories" marginBottom="large">
        <Text color="secondary60" marginBottom="medium">
          Select specific categories where this fee should apply. If no categories are selected, the fee will apply to all products. 
          Combined with "Apply Fee Per" setting above to determine calculation behavior.
        </Text>
        
        {/* Category selection controls */}
        <Flex justifyContent="space-between" alignItems="center" marginBottom="medium">
          <Text variant="h4">
            {selectedCategories.length > 0 
              ? `${selectedCategories.length} categories selected` 
              : 'All categories (no restrictions)'
            }
          </Text>
          <Flex flexGap="small">
            <Button
              variant="subtle"
              onClick={handleSelectAllCategories}
              disabled={categories.length === 0}
            >
              Select All
            </Button>
            <Button
              variant="subtle"
              onClick={handleDeselectAllCategories}
              disabled={selectedCategories.length === 0}
            >
              Clear All
            </Button>
          </Flex>
        </Flex>

        {/* Categories tree */}
        {categories.length > 0 ? (
          <Box 
            border="box" 
            borderRadius="normal" 
            padding="medium" 
            maxHeight="300px" 
            overflow="auto"
            backgroundColor="secondary10"
          >
            <CategoryTree
              categories={categories}
              selectedCategories={selectedCategories}
              onCategoryToggle={handleCategoryToggle}
            />
          </Box>
        ) : (
          <Box 
            border="box" 
            borderRadius="normal" 
            padding="medium" 
            backgroundColor="secondary10"
          >
            <Text color="secondary60">Loading categories...</Text>
          </Box>
        )}

        {/* Scope preview */}
        {selectedCategories.length > 0 && (
          <Box 
            marginTop="medium" 
            padding="small" 
            backgroundColor="primary10" 
            borderRadius="normal"
          >
            <Text color="primary" fontSize="small">
              <strong>Scope:</strong> Fee will apply to products in {selectedCategories.length} selected categor{selectedCategories.length === 1 ? 'y' : 'ies'} 
              {applyFeePer === 'order' ? ', charged once per order.' : ', charged per matching product.'}
            </Text>
          </Box>
        )}
      </Panel>

      {/* Products Panel */}
      <Panel header="Products" marginBottom="large">
        <Text color="secondary60" marginBottom="medium">
          Select specific products where this fee should apply. If no products are selected, the fee will apply based on category restrictions only.
          Both product and category restrictions work together - fees apply to products that match BOTH criteria when both are specified.
        </Text>
        
        {/* Selected products display */}
        {selectedProducts.length > 0 && (
          <Box marginBottom="medium">
            <Box 
              padding="medium"
              backgroundColor="success10"
              borderRadius="normal"
              marginBottom="small"
            >
              <Text variant="h5" color="success">
                Selected Products ({selectedProducts.length})
              </Text>
            </Box>
            
            <Table
              columns={[
                {
                  header: 'Name',
                  hash: 'name',
                  render: ({ name }) => <Text>{name}</Text>
                },
                {
                  header: 'SKU',
                  hash: 'sku',
                  render: ({ sku }) => <Text>{sku || 'N/A'}</Text>
                },
                {
                  header: 'Action',
                  hash: 'action',
                  render: (product) => (
                    <Button
                      variant="subtle"
                      size="small"
                      onClick={() => setSelectedProducts(prev => prev.filter(p => p.id !== product.id))}
                    >
                      Remove
                    </Button>
                  )
                }
              ]}
              items={selectedProducts.slice(0, 10)}
              stickyHeader
            />
            
            {selectedProducts.length > 10 && (
              <Text fontSize="small" color="secondary60" marginTop="small">
                ... and {selectedProducts.length - 10} more products
              </Text>
            )}
            
            <Flex justifyContent="flex-end" marginTop="medium">
              <Button variant="subtle" size="small" onClick={handleClearSelectedProducts}>
                Clear All Products
              </Button>
            </Flex>
          </Box>
        )}

        {/* Product search */}
        <Box marginBottom="medium">
          <FormGroup>
            <Input
              label="Search Products"
              placeholder="Search by product name or SKU..."
              value={productSearch}
              onChange={(e) => handleProductSearch(e.target.value)}
            />
          </FormGroup>
        </Box>

        {/* Product count and pagination header */}
        {!isLoadingProducts && (
          <Flex justifyContent="space-between" alignItems="center" marginTop="medium" marginBottom="medium">
            <Text fontSize="large" fontWeight="semiBold">
              {productPagination.total || 0} Products
            </Text>
            
            <Flex alignItems="center" gridGap="medium">
              <Text fontSize="medium">
                {products.length > 0 
                  ? `${(productPage - 1) * 10 + 1} - ${Math.min(productPage * 10, productPagination.total || products.length)} of ${productPagination.total || 0}`
                  : '0 of 0'
                }
              </Text>
              <Button
                variant="subtle"
                iconOnly={<ChevronLeftIcon />}
                disabled={productPage <= 1}
                onClick={() => handleProductPageChange(productPage - 1)}
              />
              <Button
                variant="subtle"
                iconOnly={<ChevronRightIcon />}
                disabled={productPage >= productPagination.total_pages || products.length === 0}
                onClick={() => handleProductPageChange(productPage + 1)}
              />
            </Flex>
          </Flex>
        )}

        {/* Product list */}
        <Box>
          {isLoadingProducts ? (
            <Box 
              border="box" 
              borderRadius="normal" 
              padding="medium"
              backgroundColor="secondary10"
            >
              <Text color="secondary60">Loading products...</Text>
            </Box>
          ) : products.length > 0 ? (
            <>
              <Table
                columns={[
                  {
                    header: 'Select',
                    hash: 'select',
                    render: (product) => (
                      <Checkbox
                        checked={selectedProducts.some(p => p.id === product.id)}
                        onChange={() => handleProductToggle(product)}
                      />
                    )
                  },
                  {
                    header: 'Name',
                    hash: 'name',
                    render: ({ name }) => <Text>{name}</Text>
                  },
                  {
                    header: 'SKU',
                    hash: 'sku',
                    render: ({ sku }) => <Text>{sku || 'N/A'}</Text>
                  }
                ]}
                items={products}
                stickyHeader
              />
              
              {/* Pagination controls */}
              {productPagination.total_pages > 1 && (
                <Flex justifyContent="space-between" alignItems="center" marginTop="medium">
                  <Button
                    variant="subtle"
                    disabled={productPage <= 1}
                    onClick={() => handleProductPageChange(productPage - 1)}
                  >
                    Previous
                  </Button>
                  <Text fontSize="small">
                    Page {productPagination.current_page} of {productPagination.total_pages} ({productPagination.total} products total)
                  </Text>
                  <Button
                    variant="subtle"
                    disabled={productPage >= productPagination.total_pages}
                    onClick={() => handleProductPageChange(productPage + 1)}
                  >
                    Next
                  </Button>
                </Flex>
              )}
            </>
          ) : (
            <Box 
              border="box" 
              borderRadius="normal" 
              padding="medium"
              backgroundColor="secondary10"
            >
              <Text color="secondary60">
                {productSearch ? `No products found for "${productSearch}"` : 'No products available'}
              </Text>
            </Box>
          )}
        </Box>

        {/* Product scope preview */}
        {selectedProducts.length > 0 && (
          <Box 
            marginTop="medium" 
            padding="small" 
            backgroundColor="primary10" 
            borderRadius="normal"
          >
            <Text color="primary" fontSize="small">
              <strong>Product Scope:</strong> Fee applies if any of the selected products are in the order
              {selectedCategories.length > 0 && (
                <> AND products in {selectedCategories.length} selected categor{selectedCategories.length === 1 ? 'y' : 'ies'}</>
              )}
              {applyFeePer === 'order' ? ', charged once per order.' : ', charged per matching product.'}
            </Text>
          </Box>
        )}
      </Panel>

      {/* Brands Panel */}
      <Panel header="Brands" marginBottom="large">
        <Text color="secondary60" marginBottom="medium">
          Select specific brands where this fee should apply. If no brands are selected, the fee will apply based on category and product restrictions only.
          When brands are specified along with categories/products, fees apply to items that match ALL criteria.
        </Text>
        
        {/* Selected brands display */}
        {selectedBrands.length > 0 && (
          <Box marginBottom="medium">
            <Box 
              padding="medium"
              backgroundColor="success10"
              borderRadius="normal"
              marginBottom="small"
            >
              <Text variant="h5" color="success">
                Selected Brands ({selectedBrands.length})
              </Text>
            </Box>
            
            <Table
              columns={[
                {
                  header: 'Name',
                  hash: 'name',
                  render: ({ name }) => <Text>{name}</Text>
                },
                {
                  header: 'Action',
                  hash: 'action',
                  render: (brand) => (
                    <Button
                      variant="subtle"
                      size="small"
                      onClick={() => setSelectedBrands(prev => prev.filter(b => b.id !== brand.id))}
                    >
                      Remove
                    </Button>
                  )
                }
              ]}
              items={selectedBrands.slice(0, 10)}
              stickyHeader
            />
            
            {selectedBrands.length > 10 && (
              <Text fontSize="small" color="secondary60" marginTop="small">
                ... and {selectedBrands.length - 10} more brands
              </Text>
            )}
            
            <Flex justifyContent="flex-end" marginTop="medium">
              <Button variant="subtle" size="small" onClick={handleClearSelectedBrands}>
                Clear All Brands
              </Button>
            </Flex>
          </Box>
        )}

        {/* Brand search */}
        <Box marginBottom="medium">
          <FormGroup>
            <Input
              label="Search Brands"
              placeholder="Search by brand name..."
              value={brandSearch}
              onChange={(e) => handleBrandSearch(e.target.value)}
            />
          </FormGroup>
        </Box>

        {/* Brand count and pagination header */}
        {!isLoadingBrands && (
          <Flex justifyContent="space-between" alignItems="center" marginTop="medium" marginBottom="medium">
            <Text fontSize="large" fontWeight="semiBold">
              {brandPagination.total || 0} Brands
            </Text>
            
            <Flex alignItems="center" gridGap="medium">
              <Text fontSize="medium">
                {brands.length > 0 
                  ? `${(brandPage - 1) * 10 + 1} - ${Math.min(brandPage * 10, brandPagination.total || brands.length)} of ${brandPagination.total || 0}`
                  : '0 of 0'
                }
              </Text>
              <Button
                variant="subtle"
                iconOnly={<ChevronLeftIcon />}
                disabled={brandPage <= 1}
                onClick={() => handleBrandPageChange(brandPage - 1)}
              />
              <Button
                variant="subtle"
                iconOnly={<ChevronRightIcon />}
                disabled={brandPage >= brandPagination.total_pages || brands.length === 0}
                onClick={() => handleBrandPageChange(brandPage + 1)}
              />
            </Flex>
          </Flex>
        )}

        {/* Brand list */}
        <Box>
          {isLoadingBrands ? (
            <Box 
              border="box" 
              borderRadius="normal" 
              padding="medium"
              backgroundColor="secondary10"
            >
              <Text color="secondary60">Loading brands...</Text>
            </Box>
          ) : brands.length > 0 ? (
            <>
              <Table
                columns={[
                  {
                    header: 'Select',
                    hash: 'select',
                    render: (brand) => (
                      <Checkbox
                        checked={selectedBrands.some(b => b.id === brand.id)}
                        onChange={() => handleBrandToggle(brand)}
                      />
                    )
                  },
                  {
                    header: 'Name',
                    hash: 'name',
                    render: ({ name }) => <Text>{name}</Text>
                  }
                ]}
                items={brands}
                stickyHeader
              />
              
              {/* Pagination controls */}
              {brandPagination.total_pages > 1 && (
                <Flex justifyContent="space-between" alignItems="center" marginTop="medium">
                  <Button
                    variant="subtle"
                    disabled={brandPage <= 1}
                    onClick={() => handleBrandPageChange(brandPage - 1)}
                  >
                    Previous
                  </Button>
                  <Text fontSize="small">
                    Page {brandPage} of {brandPagination.total_pages}
                  </Text>
                  <Button
                    variant="subtle"
                    disabled={brandPage >= brandPagination.total_pages}
                    onClick={() => handleBrandPageChange(brandPage + 1)}
                  >
                    Next
                  </Button>
                </Flex>
              )}
            </>
          ) : (
            <Box 
              border="box" 
              borderRadius="normal" 
              padding="medium"
              backgroundColor="secondary10"
            >
              <Text color="secondary60">
                {brandSearch 
                  ? `No brands found matching "${brandSearch}"`
                  : "No brands available"
                }
              </Text>
            </Box>
          )}
        </Box>

        {/* Scope message */}
        {selectedBrands.length > 0 && (
          <Box 
            marginTop="medium" 
            padding="small" 
            backgroundColor="primary10" 
            borderRadius="normal"
          >
            <Text color="primary" fontSize="small">
              <strong>Brand Scope:</strong> Fee applies if any products from the selected brands are in the order
              {selectedCategories.length > 0 && (
                <> AND products in {selectedCategories.length} selected categor{selectedCategories.length === 1 ? 'y' : 'ies'}</>
              )}
              {selectedProducts.length > 0 && (
                <> AND any of the {selectedProducts.length} selected product{selectedProducts.length === 1 ? '' : 's'}</>
              )}
              {applyFeePer === 'order' ? ', charged once per order.' : ', charged per matching product.'}
            </Text>
          </Box>
        )}
      </Panel>

      {/* Channels Panel */}
      <Panel header="Channels" marginBottom="large">
        <Text color="secondary60" marginBottom="medium">
          Select specific sales channels where this fee should apply. If no channels are selected, the fee will apply to all sales channels.
        </Text>
        
        {/* Selected channels display */}
        {selectedChannels.length > 0 && (
          <Box marginBottom="medium">
            <Box 
              padding="medium"
              backgroundColor="success10"
              borderRadius="normal"
              marginBottom="small"
            >
              <Text variant="h5" color="success">
                Selected Channels ({selectedChannels.length})
              </Text>
            </Box>
            
            <Table
              columns={[
                {
                  header: 'Name',
                  hash: 'name',
                  render: ({ name }) => <Text>{name}</Text>
                },
                {
                  header: 'Action',
                  hash: 'action',
                  render: (channel) => (
                    <Button
                      variant="subtle"
                      size="small"
                      onClick={() => setSelectedChannels(prev => prev.filter(c => c.id !== channel.id))}
                    >
                      Remove
                    </Button>
                  )
                }
              ]}
              items={selectedChannels}
              stickyHeader
            />
            
            <Flex justifyContent="flex-end" marginTop="medium">
              <Button variant="subtle" size="small" onClick={handleClearSelectedChannels}>
                Clear All Channels
              </Button>
            </Flex>
          </Box>
        )}

        {/* Available channels */}
        <Box marginBottom="medium">
          <Text fontSize="large" fontWeight="semiBold" marginBottom="medium">
            Available Channels ({channels.length})
          </Text>
          
          {channels.length > 0 ? (
            <Table
              columns={[
                {
                  header: 'Select',
                  hash: 'select',
                  render: (channel) => (
                    <Checkbox
                      checked={selectedChannels.some(c => c.id === channel.id)}
                      onChange={() => handleChannelToggle(channel)}
                    />
                  )
                },
                {
                  header: 'Name',
                  hash: 'name',
                  render: ({ name }) => <Text>{name}</Text>
                },
                {
                  header: 'Type',
                  hash: 'type',
                  render: ({ type }) => <Text>{type.charAt(0).toUpperCase() + type.slice(1)}</Text>
                },
                {
                  header: 'Platform',
                  hash: 'platform',
                  render: ({ platform }) => <Text>{platform === 'bigcommerce' ? 'BigCommerce' : platform.charAt(0).toUpperCase() + platform.slice(1)}</Text>
                }
              ]}
              items={channels}
              stickyHeader
            />
          ) : (
            <Box 
              border="box" 
              borderRadius="normal" 
              padding="medium"
              backgroundColor="secondary10"
            >
              <Text color="secondary60">No channels available</Text>
            </Box>
          )}
        </Box>

        {/* Scope message */}
        {selectedChannels.length > 0 && (
          <Box 
            marginTop="medium" 
            padding="small" 
            backgroundColor="primary10" 
            borderRadius="normal"
          >
            <Text color="primary" fontSize="small">
              <strong>Channel Scope:</strong> Fee applies only to orders placed on {selectedChannels.map(c => c.name).join(', ')}
              {applyFeePer === 'order' ? ', charged once per order.' : ', charged per qualifying product.'}
            </Text>
          </Box>
        )}
      </Panel>

      {/* Customer Groups Panel */}
      <Panel header="Customer Groups" marginBottom="large">
        <Text color="secondary60" marginBottom="medium">
          Select specific customer groups where this fee should apply. If no customer groups are selected, the fee will apply to all customers.
        </Text>
        
        {/* Selected customer groups display */}
        {selectedCustomerGroups.length > 0 && (
          <Box marginBottom="medium">
            <Box 
              padding="medium"
              backgroundColor="success10"
              borderRadius="normal"
              marginBottom="small"
            >
              <Text variant="h5" color="success">
                Selected Customer Groups ({selectedCustomerGroups.length})
              </Text>
            </Box>
            
            <Table
              columns={[
                {
                  header: 'Name',
                  hash: 'name',
                  render: ({ name }) => <Text>{name}</Text>
                },
                {
                  header: 'Action',
                  hash: 'action',
                  render: (group) => (
                    <Button
                      variant="subtle"
                      size="small"
                      onClick={() => setSelectedCustomerGroups(prev => prev.filter(g => g.id !== group.id))}
                    >
                      Remove
                    </Button>
                  )
                }
              ]}
              items={selectedCustomerGroups}
              stickyHeader
            />
            
            <Flex justifyContent="flex-end" marginTop="medium">
              <Button variant="subtle" size="small" onClick={handleClearSelectedCustomerGroups}>
                Clear All Customer Groups
              </Button>
            </Flex>
          </Box>
        )}

        {/* Available customer groups */}
        <Box marginBottom="medium">
          <Text fontSize="large" fontWeight="semiBold" marginBottom="medium">
            Available Customer Groups ({customerGroups.length})
          </Text>
          
          {customerGroups.length > 0 ? (
            <Table
              columns={[
                {
                  header: 'Select',
                  hash: 'select',
                  render: (group) => (
                    <Checkbox
                      checked={selectedCustomerGroups.some(g => g.id === group.id)}
                      onChange={() => handleCustomerGroupToggle(group)}
                    />
                  )
                },
                {
                  header: 'Name',
                  hash: 'name',
                  render: ({ name }) => <Text>{name}</Text>
                },
                {
                  header: 'Default',
                  hash: 'is_default',
                  render: ({ is_default }) => <Text>{is_default ? 'Yes' : 'No'}</Text>
                }
              ]}
              items={customerGroups}
              stickyHeader
            />
          ) : (
            <Box 
              border="box" 
              borderRadius="normal" 
              padding="medium"
              backgroundColor="secondary10"
            >
              <Text color="secondary60">No customer groups available</Text>
            </Box>
          )}
        </Box>

        {/* Scope message */}
        {selectedCustomerGroups.length > 0 && (
          <Box 
            marginTop="medium" 
            padding="small" 
            backgroundColor="primary10" 
            borderRadius="normal"
          >
            <Text color="primary" fontSize="small">
              <strong>Customer Group Scope:</strong> Fee applies only to customers in {selectedCustomerGroups.map(g => g.name).join(', ')} groups.
              Guest customers will {selectedCustomerGroups.length > 0 ? 'not' : ''} be charged this fee.
            </Text>
          </Box>
        )}
      </Panel>

      {/* Action Buttons */}
      <Flex justifyContent="flex-end" flexGap="medium">
        <Button variant="subtle" onClick={handleCancel}>
          Cancel
        </Button>
        <Button 
          variant="primary" 
          onClick={handleSave}
          isLoading={isSaving}
        >
          {isSaving ? (isEdit ? 'Updating...' : 'Creating...') : (isEdit ? 'Update Fee' : 'Create Fee')}
        </Button>
      </Flex>
    </Box>
  );
}

export default CreateEditFeePage;